import React from 'react';
import type { IconBaseProps, IconType } from 'react-icons';

/**
 * The accent a browse tile is tinted with. These are the five jewel tones
 * declared in theme.css; the name is interpolated into `var(--accent-<name>)`,
 * so the union must stay in step with that file.
 */
export type BrowseAccent = 'gold' | 'indigo' | 'purple' | 'emerald' | 'rose';

/**
 * The leading glyph on a /browse tile.
 *
 * The glyphs are Ionicons 5, deliberately the SAME set the native app's browse
 * root uses (`react-native-vector-icons/Ionicons`) so the two surfaces name the
 * same thing with the same picture. react-icons is already a dependency, so
 * this costs no new package — see src/browse/facets.ts for the mapping.
 *
 * The chip itself is web's own aesthetic, not native's: the tint is drawn as a
 * separate `bg-current` layer rather than a pre-mixed hex, which means one
 * `color` declaration drives both the glyph and its wash, and the wash can
 * brighten on hover with nothing but an opacity transition.
 */
export const BrowseIcon: React.FC<{ icon: IconType; accent: BrowseAccent }> = ({
  icon,
  accent,
}) => {
  /*
   * react-icons types IconType as returning ReactNode. TypeScript only learned
   * to accept such a component in JSX in 5.1 (JSX.ElementType); CRA pins this
   * app to 4.9.5, so `<Icon />` on a bare IconType is TS2786.
   *
   * The narrowing is provably safe rather than a papered-over lie: every
   * concrete icon is produced by GenIcon, which react-icons itself declares as
   * returning React.JSX.Element (react-icons/lib/iconBase.d.ts:9). IconType is
   * merely the wider public alias. Keeping the cast here means no call site
   * needs to know, and it can be deleted wholesale when the app leaves CRA.
   */
  const Glyph = icon as React.FC<IconBaseProps>;

  return (
    <span
      className="relative grid h-10 w-10 shrink-0 place-items-center rounded-xl"
      style={{ color: `var(--accent-${accent})` }}
    >
      <span
        aria-hidden
        className="absolute inset-0 rounded-xl bg-current opacity-[0.14] transition-opacity duration-250 ease-out-quint group-hover:opacity-25"
      />
      {/*
        aria-hidden: the tile's accessible name must stay "<title> <subtitle>".
        react-icons only marks the svg hidden when it is given no `title`, and
        relying on that default would make a future `title` prop silently rename
        every link.
      */}
      <Glyph size={20} aria-hidden className="relative" />
    </span>
  );
};
